import { useState } from 'react'
import {
  MANAGED_SETTING_KEYS,
  NON_SECRET_SETTING_KEYS,
  useAppSettings,
  useSaveAppSetting,
  type SettingView,
} from '@/hooks/useAdminActions'
import { AdminError } from './AdminError'

function updatedLabel(setting: SettingView | undefined) {
  if (!setting?.updated_at) return 'never'
  return new Date(setting.updated_at).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

function SettingRow({ settingKey, setting }: { settingKey: string; setting: SettingView | undefined }) {
  const save = useSaveAppSetting()
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState(false)
  const [reveal, setReveal] = useState(false)

  const isSecret = !NON_SECRET_SETTING_KEYS.includes(settingKey)
  const isSet = !!setting?.isSet
  const missing = !setting

  function commit() {
    if (!draft.trim()) return
    save.mutate({ key: settingKey, value: draft.trim() }, {
      onSuccess: () => { setDraft(''); setEditing(false); setReveal(false) },
    })
  }

  return (
    <div className="admin-key-row">
      <div className="admin-key-row__head">
        <div>
          <span className="admin-key-row__name mono">{settingKey}</span>
          {setting?.description && <p className="admin-key-row__desc">{setting.description}</p>}
        </div>
        <span className={`status-pill ${missing ? 'status--error' : isSet ? 'status--ready' : 'status--none'}`}>
          {missing ? 'Missing row' : isSet ? 'Set' : 'Not set'}
        </span>
      </div>

      <div className="admin-key-row__meta">
        <span className="admin-key-row__value mono">
          {!isSet ? 'Not set' : isSecret || !reveal ? '••••••••••••' : setting?.value}
        </span>
        {isSet && !isSecret && (
          <button className="admin-link-btn" onClick={() => setReveal((r) => !r)}>
            {reveal ? 'Hide' : 'Show'}
          </button>
        )}
        <span className="admin-key-row__updated">Updated {updatedLabel(setting)}</span>
      </div>

      {missing ? (
        <p className="admin-field__hint admin-field__hint--error">
          No <code>app_settings</code> row for this key. It must be seeded by a migration before it can be set.
        </p>
      ) : editing ? (
        <div className="admin-inline-form">
          <input
            className="admin-input mono"
            type={isSecret ? 'password' : 'text'}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={isSet ? 'Enter a replacement value' : 'Enter a value'}
            autoComplete="off"
            spellCheck={false}
          />
          <button className="btn-primary btn--amber" onClick={commit} disabled={!draft.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className="btn-secondary" onClick={() => { setDraft(''); setEditing(false) }} disabled={save.isPending}>
            Cancel
          </button>
        </div>
      ) : (
        <div className="admin-row-actions">
          <button className="admin-link-btn" onClick={() => setEditing(true)}>
            {isSet ? 'Replace value' : 'Set value'}
          </button>
          {isSet && (
            <button
              className="admin-link-btn admin-link-btn--danger"
              onClick={() => save.mutate({ key: settingKey, value: null })}
              disabled={save.isPending}
            >
              Clear
            </button>
          )}
        </div>
      )}

      <AdminError error={save.error} />
    </div>
  )
}

export function ApiKeysPanel() {
  const { data: settings, isLoading, error } = useAppSettings()
  const byKey = new Map((settings ?? []).map((s) => [s.key, s]))

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">API Keys</h2>
      <p className="admin-field__hint">
        The pipeline reads these with the service role when the matching environment variable isn't set.
        Secret values are discarded on load and never rendered. A secret can be replaced or cleared, not read back.
      </p>

      <AdminError error={error} />

      {isLoading ? (
        <p className="admin-empty-hint">Loading settings…</p>
      ) : (
        <div className="admin-key-list">
          {MANAGED_SETTING_KEYS.map((key) => (
            <SettingRow key={key} settingKey={key} setting={byKey.get(key)} />
          ))}
        </div>
      )}
    </div>
  )
}
